import { useState, useEffect } from 'react';
import { Select } from 'antd';
import SimpleInput from 'ui/input';
import MultiSelect from 'ui/multiSelect';
import DadataAddress from 'components/dadata/address';
import FacilityMap from 'pages/facility/components/mapAndCoordinates/map';
import { reqCoordinates } from 'utils/helpers/constVariables';
import { useMapStore } from 'pages/facility/lib/hooks/useMapStore';
import { useGetCityMetroLinesWithStationsLazyQuery } from 'pages/facility/api/queries/generated/GetCityMetroLinesWithStations';
import { useGetCitiesQuery } from 'graphql/queries/generated/GetCities';
import { timezoneList } from 'utils/helpers/lists/timezoneList';
import { useParams } from 'react-router';

interface MapSectionProps {
  values: any;
  touched: any;
  errors: any;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setFieldValue: (field: string, value: any) => void;
  isFieldInherited: (fieldName: string) => boolean;
}

const MapSection = ({ values, touched, errors, handleChange, setFieldValue, isFieldInherited }: MapSectionProps) => {
  const { facilityId } = useParams();

  const { setLongitude, setLatitude, latitude, longitude } = useMapStore();
  const [citySearch, setCitySearch] = useState('');

  const { data: citiesData, fetchMore: fetchMoreCities } = useGetCitiesQuery({
    variables: {
      input: {
        limit: 20,
        offset: 0,
        ...(citySearch ? { searchQuery: citySearch } : {}),
      },
    },
  });

  const [loadMetroStations, { data: metroStationsData }] = useGetCityMetroLinesWithStationsLazyQuery();

  useEffect(() => {
    if (values.cityId) {
      loadMetroStations({
        variables: {
          input: {
            cityId: values.cityId,
          },
        },
      });
    }
  }, [values.cityId, loadMetroStations]);

  const handleCityScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const isNearBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50;

    if (isNearBottom) {
      fetchMoreCities({
        variables: {
          input: {
            limit: 20,
            offset: citiesData?.getCities.cities.length || 0,
            ...(citySearch ? { searchQuery: citySearch } : {}),
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            getCities: {
              ...prev.getCities,
              cities: [...prev.getCities.cities, ...fetchMoreResult.getCities.cities],
            },
          };
        },
      });
    }
  };

  return (
    <div className="col-span-full">
      <span className="inline-block Subtitle1 text-smena_text-secondary mb-4">Местоположение</span>
      <div className="grid grid-cols-2 gap-5">
        <div className="w-full">
          <label className="label-primary">Часовой пояс</label>
          <Select
            className="w-full"
            showSearch
            placeholder="Выберите часовой пояс"
            optionFilterProp="children"
            onChange={value => setFieldValue('timezone', value)}
            value={values.timezone}
            onSearch={value => setCitySearch(value)}
            filterOption={false}
            onPopupScroll={handleCityScroll}
            listHeight={200}
            disabled={isFieldInherited('timezone') || !!facilityId}
          >
            {timezoneList.map(timezone => (
              <Select.Option key={timezone.id} value={timezone.id}>
                {timezone.name}
              </Select.Option>
            ))}
          </Select>
          {touched.timezone && errors.timezone && <div className="error-message">{errors.timezone}</div>}
        </div>

        <div className="w-full">
          <label className="label-primary">Город</label>
          <Select
            className="w-full"
            showSearch
            placeholder="Выберите город"
            optionFilterProp="children"
            onChange={value => setFieldValue('cityId', value)}
            value={values.cityId}
            onSearch={value => setCitySearch(value)}
            filterOption={false}
            onPopupScroll={handleCityScroll}
            listHeight={200}
            disabled={isFieldInherited('cityId')}
          >
            {citiesData?.getCities.cities.map(city => (
              <Select.Option key={city.id} value={city.id}>
                {city.name}
              </Select.Option>
            ))}
          </Select>
          {touched.cityId && errors.cityId && <div className="error-message">{errors.cityId}</div>}
        </div>
      </div>

      {!values.allowInheritance && (
        <>
          <div className="mt-5">
            <DadataAddress
              label="Адрес"
              placeholder="Введите адрес"
              value={values.addressValue}
              textValue={values.addressValue}
              onChange={value => {
                if (!value) return;
                setFieldValue('address', value.value);
                setFieldValue('addressValue', value);
                setFieldValue('coordinates', {
                  lat: value.data.geo_lat,
                  lon: value.data.geo_lon,
                });
                setLatitude(value.data.geo_lat);
                setLongitude(value.data.geo_lon);
              }}
              error={touched.address && errors.address ? errors.address : ''}
            />
          </div>

          <div className="mt-5">
            <MultiSelect
              divClassName="w-full"
              label="Станции метро"
              placeholder="Выберите станции метро"
              onChange={value => setFieldValue('metroStationIds', value)}
              value={values.metroStationIds || []}
              options={
                metroStationsData?.getCityMetroLinesWithStations?.flatMap(line =>
                  line.metroStations.map(station => ({
                    value: station.id,
                    label: station.name,
                  })),
                ) || []
              }
              name="metroStationIds"
              error={touched.metroStationIds && errors.metroStationIds ? errors.metroStationIds : ''}
              disabled={!values.cityId}
            />
          </div>

          <div className="mt-5">
            <div className="h-52 w-full relative">
              <FacilityMap />
            </div>
            <div className="mt-5 grid grid-cols-8 gap-5">
              <SimpleInput
                divClassName="col-span-3"
                placeholder="Введите широту"
                label="Широта"
                onChange={e => {
                  const value = e.target.value;
                  if (reqCoordinates.test(value) || value === '') {
                    setLatitude(value);
                  }
                }}
                value={latitude ?? ''}
                type="text"
                name="latitude"
                error={errors.latitude ?? ''}
              />

              <SimpleInput
                divClassName="col-span-3"
                placeholder="Введите долготу"
                label="Долгота"
                onChange={e => {
                  const value = e.target.value;
                  if (reqCoordinates.test(value) || value === '') {
                    setLongitude(value);
                  }
                }}
                value={longitude ?? ''}
                type="text"
                name="longitude"
                error={errors.longitude ?? ''}
              />

              <SimpleInput
                divClassName="col-span-2"
                placeholder="Введите радиус"
                label="Радиус (м.)"
                onChange={handleChange}
                value={values.radius || ''}
                type="text"
                name="radius"
                error={touched.radius && errors.radius ? errors.radius : ''}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MapSection;
